import * as React from "react";
import * as Storage from "aws-amplify/storage";
import {
  Formik,
  FormikActions
} from "formik";
import { toast } from "react-toastify";
import { useDispatch } from "redux-react-hook";
import * as yup from "yup";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import { Switch } from "src/library/components/Forms/Switch";
import { Button } from "src/library/components/Button";
import {
  FormRow,
  Input,
  InputImage,
  Label,
  Select,
  TextArea
} from "src/library/components/Forms";
import ProductInfoSidebar from "src/core/products/components/ProductInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { useProductsApi } from "src/core/products/api/products.api";
import { setProduct } from "src/core/products/store/products.actions";
import { PageProps } from "src/pages/Router/routes";
import { ImageCroppingModal } from "src/library/components/Modal";
import { useCurrentUser } from "src/core/users/hooks";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { useSuppliersApi } from "src/core/suppliers/api/suppliers.api";

interface ProductFormValues {
  productImage: File | null;
  nutritionalImage: File | null;
  currentPrice: number;
  leadTimeGroupId: string;
  description: string;
  ingredients?: string;
  published: string;
  infoKey?: string;
  partNumber: string;
  vatPercentage: number;
  madeToOrder: boolean;
  barcode: string;
  minimumQuantity?: number | null;
  weight?: number | null;
  supplierPartNumber?: string | null;
  supplierId?: string | null;
  creditOnly: boolean;
}

const published = [ "Not published", "Published" ];

const productSchema = yup.object().shape({
  productImage: yup.mixed().nullable(),
  nutritionalImage: yup.mixed().nullable(),
  madeToOrder: yup.boolean().required(),
  partNumber: yup
    .string()
    .min(1)
    .required("Required"),
  description: yup
    .string()
    .min(1)
    .required("Required"),
  currentPrice: yup.number().required("Required"),
  // packSize: yup.number().required("Required"),
  leadTimeGroupId: yup.string().nullable(),
  published: yup.string().required(),
  creditOnly: yup
    .boolean()
    .required()
    .nullable(),
  ingredients: yup.string(),
  vatPercentage: yup.number(),
  barcode: yup.string(),
  minimumQuantity: yup.number().nullable(),
  weight: yup.number().nullable(),
  supplierPartNumber: yup.string().nullable()
});

const CreateProductPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology, history } = props;
  const dispatch = useDispatch();
  const [ createProductResponse, createProduct ] = useProductsApi("CREATE_PRODUCT");
  const [ suppliersRes, suppliersReq ] = useSuppliersApi("LIST_SUPPLIERS");
  const [ productImportSettingRes, productImportSettingReq ] = useImportSettingApi("GET_IMPORT_SETTING");
  const [ imageCropperModalFile, setImageCropperModalFile ] = React.useState<File | null>(null);
  const [ { currentRole } ] = useCurrentUser();
  const [ { globalSettings } ] = useGlobalSettings();

  React.useEffect(() => {
    productImportSettingReq({
      type: ImportSettingType.Product,
      origin: ImportOrigin.CSV
    });
  }, [ productImportSettingReq ]);

  const supplierHeaderMapping = React.useMemo(() => {
    return productImportSettingRes?.data?.headerMappings.find(headerMapping => headerMapping.internal === "SupplierSlug");
  }, [ productImportSettingRes ]);

  // Fetch list of suppliers on mount
  React.useEffect(() => {
    suppliersReq();
  }, [ suppliersReq ]);

  React.useEffect(() => {
    // On Success reset the form
    if (createProductResponse.data) {
      toast.success("Successfully created product.");
      dispatch(setProduct(createProductResponse.data));
      history.push(`/admin/products/${createProductResponse.data.id}/details`);
    }

    // On error
    if (createProductResponse.error) {
      toast.error(createProductResponse.error.message);
    }
  }, [
    createProductResponse.complete,
    createProductResponse.data,
    createProductResponse.error
  ]);

  const submitForm = async (values: ProductFormValues, actions: FormikActions<ProductFormValues>) => {
    const imageDir = "product-images";
    let image = "";
    let nutritionImage = "";

    if (values.productImage) {
      try {
        const result = await Storage.uploadData({
          path: `public/${imageDir}/main/${values.productImage.name}`,
          data: values.productImage,
          options: { contentType: values.productImage.type }
        }).result;

        image = result.path.replace("public/", "");
      } catch (error) {
        actions.setError("There was an error uploading your image");

        throw error;
      }
    }

    if (values.nutritionalImage) {
      try {
        const result = await Storage.uploadData({
          path: `public/${imageDir}/nutrition/${values.nutritionalImage.name}`,
          data: values.nutritionalImage,
          options: { contentType: values.nutritionalImage.type }
        }).result;

        nutritionImage = result.path.replace("public/", "");
      } catch (error) {
        actions.setError("There was an error uploading your image");

        throw error;
      }
    }

    createProduct({
      description: values.description,
      image,
      partNumber: values.partNumber,
      published: values.published === "published",
      leadTimeGroupId: parseInt(values.leadTimeGroupId, 10),
      ingredients: values.ingredients,
      nutritionImage,
      infoKey: values.infoKey || "",
      price: values.currentPrice,
      vatPercentage: values.vatPercentage,
      madeToOrder: values.madeToOrder,
      barcode: values.barcode,
      minimumQuantity: values.minimumQuantity,
      weight: values.weight,
      creditOnly: values.creditOnly,
      supplierPartNumber: values.supplierPartNumber,
      supplierId: values.supplierId ? parseInt(values.supplierId, 10) : null
    });
  };

  // TODO: Fix error and submitting fields
  // TODO: Add sub text to input field
  return (
    <Formik
      initialValues={{
        description: "",
        productImage: null,
        nutritionalImage: null,
        currentPrice: 0,
        leadTimeGroupId: "",
        published: "published",
        infoKey: "",
        ingredients: "",
        partNumber: "",
        vatPercentage: 0,
        madeToOrder: false,
        barcode: "",
        minimumQuantity: null,
        supplierId: "",
        weight: null,
        supplierPartNumber: null,
        creditOnly: false
      }}
      validationSchema={productSchema}
      onSubmit={submitForm}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({
        values, handleSubmit, handleChange, setFieldValue, errors
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Layout {...props}>
              <MainContentWrapper fill>
                <FormSection>
                  <h2>
                    Key Information
                  </h2>

                  <Grid.Bounds style={{ flexWrap: "wrap" }}>
                    <Grid.Box style={{ paddingRight: "20px" }}>
                      <InputImage
                        label={`${terminology.product.singular.text} Image`}
                        autoHideLabels
                        name="productImage"
                        handleChange={evt => setImageCropperModalFile(evt.currentTarget.files[ 0 ])}
                        value={values.productImage}
                      />
                    </Grid.Box>

                    <Grid.Box>
                      <FormRow>
                        <Input
                          value={values.description}
                          error={errors.description}
                          label={`${terminology.product.singular.text} name`}
                          name="description"
                          handleChange={handleChange}
                        />
                      </FormRow>

                      <FormRow>
                        <Grid.Bounds direction="horizontal">
                          <Input
                            value={values.partNumber}
                            label={terminology?.partNumber.singular.text || "Part number"}
                            name="partNumber"
                            handleChange={handleChange}
                            error={errors.partNumber}
                          />
                        </Grid.Bounds>
                      </FormRow>
                    </Grid.Box>
                  </Grid.Bounds>

                  <Grid.Bounds
                    margin="30px 0"
                    direction="vertical"
                  >
                    <FormRow>
                      <Grid.Bounds direction="horizontal">
                        <Input
                          label="Barcode"
                          value={values.barcode}
                          name="barcode"
                          handleChange={handleChange}
                          error={errors.barcode}
                        />
                      </Grid.Bounds>
                    </FormRow>

                    <FormRow>
                      <Grid.Bounds
                        direction="horizontal"
                        className="price-wrapper"
                      >
                        <Input
                          label="Default price"
                          Prepend={(
                            <InputSub
                              width="40"
                              borderright="true"
                            >
                              <p>
                                £
                              </p>
                            </InputSub>
                          )}
                          value={values.currentPrice}
                          name="currentPrice"
                          handleChange={handleChange}
                          error={errors.currentPrice}
                        />
                      </Grid.Bounds>

                      <SubText>
                        <sub>
                          This price may be overridden by price lists
                        </sub>
                      </SubText>
                    </FormRow>

                    <FormRow>
                      <Grid.Bounds
                        direction="horizontal"
                        className="vatPercentage-wrapper"
                      >
                        <Input
                          label="Default VAT"
                          inputType="number"
                          Append={(
                            <InputSub
                              width="40"
                              borderright="true"
                            >
                              <p>
                                %
                              </p>
                            </InputSub>
                          )}
                          value={values.vatPercentage}
                          name="vatPercentage"
                          handleChange={handleChange}
                          error={errors.vatPercentage}
                        />
                      </Grid.Bounds>

                      <SubText>
                        <sub>
                          This may be overridden by price lists
                        </sub>
                      </SubText>
                    </FormRow>

                    <FormRow>
                      <Grid.Bounds direction="horizontal">
                        <Input
                          label="Minimum Order Quantity"
                          inputType="number"
                          value={values.minimumQuantity}
                          name="minimumQuantity"
                          handleChange={handleChange}
                          error={errors.minimumQuantity}
                        />
                      </Grid.Bounds>
                    </FormRow>

                    <FormRow>
                      <Grid.Bounds direction="horizontal">
                        <Input
                          inputType="number"
                          value={values.weight || ""}
                          label="Product Weight (KG)"
                          name="weight"
                          handleChange={handleChange}
                          error={errors.weight}
                        />
                      </Grid.Bounds>
                    </FormRow>

                    <FormRow>
                      <Grid.Bounds
                        direction="horizontal"
                        style={{ alignItems: "center" }}
                      >
                        <Label
                          for="madeToOrder"
                          label="Made to order"
                        />
                      </Grid.Bounds>

                      <Switch
                        onChange={e => {
                          setFieldValue("madeToOrder", e);
                        }}
                        checked={values.madeToOrder}
                      />
                    </FormRow>

                    {!values.madeToOrder && (
                      <FormRow>
                        <Grid.Bounds
                          direction="horizontal"
                          className="leadTimeGroup-wrapper"
                        >
                          <Select
                            value={values.leadTimeGroupId}
                            name="leadTimeGroupId"
                            handleChange={handleChange}
                            label="Lead time"
                            options={(globalSettings?.leadTimeGroups || []).map(l => ({
                              text: l.name.toString(),
                              value: l.id.toString()
                            }))}
                            error={errors.leadTimeGroupId}
                          />

                          <InputSub
                            width="50"
                            borderright="false"
                          >
                            days
                          </InputSub>
                        </Grid.Bounds>
                      </FormRow>
                    )}
                  </Grid.Bounds>
                </FormSection>

                <FormRow>
                  <Select
                    value={values.supplierId}
                    error={errors.supplierId}
                    name="supplierId"
                    handleChange={handleChange}
                    label={supplierHeaderMapping?.external || "Supplier"}
                    options={(suppliersRes.data || []).map(s => ({
                      text: s.name,
                      value: s.id.toString()
                    }))}
                    unselectedOption={{
                      text: "No supplier associated",
                      value: ""
                    }}
                  />
                </FormRow>

                <FormRow>
                  <Grid.Bounds direction="horizontal">
                    <Input
                      value={values.supplierPartNumber || ""}
                      label={`Supplier ${terminology.partNumber.singular.text}`}
                      name="supplierPartNumber"
                      handleChange={handleChange}
                      error={errors.supplierPartNumber}
                    />
                  </Grid.Bounds>
                </FormRow>

                <FormRow>
                  <Grid.Bounds
                    direction="horizontal"
                    style={{ alignItems: "center" }}
                  >
                    <Label
                      for="creditOnly"
                      label="Allow for crediting only"
                    />
                  </Grid.Bounds>

                  <Switch
                    onChange={e => {
                      setFieldValue("creditOnly", e);
                    }}
                    checked={values.creditOnly}
                  />
                </FormRow>

                <FormSection>
                  <h2>
                    Nutritional Image
                  </h2>

                  <FormRow>
                    <Grid.Box style={{ paddingRight: "20px" }}>
                      <InputImage
                        label="Nutrition image"
                        autoHideLabels
                        name="nutritionalImage"
                        handleChange={evt => setFieldValue("nutritionalImage", evt.currentTarget.files[ 0 ])}
                        value={values.nutritionalImage}
                      />
                    </Grid.Box>
                  </FormRow>
                </FormSection>

                <FormSection>
                  <Grid.Bounds
                    margin="30px 0"
                    direction="vertical"
                  >
                    <h2>
                      Visibility
                    </h2>

                    <Grid.Box
                      style={{
                        paddingRight: "10px",
                        marginTop: 24
                      }}
                    >
                      <FormRow>
                        <Select
                          value={values.published}
                          error={errors.published}
                          name="published"
                          handleChange={handleChange}
                          label="Published"
                          options={published.map(s => ({
                            text: s,
                            value: s.toLowerCase()
                          }))}
                        />
                      </FormRow>
                    </Grid.Box>
                  </Grid.Bounds>
                </FormSection>

                <FormSection>
                  <h2>
                    Additional Information
                  </h2>

                  <Grid.Bounds
                    margin="30px 0"
                    direction="vertical"
                  >
                    <FormRow>
                      <Grid.Bounds
                        direction="horizontal"
                        style={{ alignItems: "center" }}
                      >
                        <Label
                          for="infoKey"
                          label={`About this ${terminology.product.singular.text}`}
                        />
                      </Grid.Bounds>

                      <TextArea
                        wysiwyg
                        setFieldValue={setFieldValue}
                        name="infoKey"
                        label="Additional information"
                        autoHideLabels
                        error={errors.infoKey}
                      />
                    </FormRow>

                    <FormRow>
                      <Grid.Bounds
                        direction="horizontal"
                        style={{ alignItems: "center" }}
                      >
                        <Label
                          for="ingredients"
                          label="Ingredient list"
                        />
                      </Grid.Bounds>

                      <TextArea
                        wysiwyg
                        setFieldValue={setFieldValue}
                        name="ingredients"
                        label="Ingredient list"
                        autoHideLabels
                        error={errors.ingredients}
                      />
                    </FormRow>
                  </Grid.Bounds>
                </FormSection>
              </MainContentWrapper>

              <InfoBarWrapper>
                <ProductInfoSidebar
                  terminology={terminology}
                  isLoading={false}
                  isAdmin={currentRole?.type === "admin"}
                  supplierDisplayTerm={supplierHeaderMapping?.external || "Supplier"}
                  product={{
                    ...values,
                    leadTimeGroupId: parseInt(values.leadTimeGroupId, 10),
                    published: values.published === "published",
                    supplierId: values.supplierId ? parseInt(values.supplierId, 10) : null,
                    // include the supplier so name can be displayed
                    supplier: suppliersRes.data?.find(s => s.id === parseInt(values.supplierId, 10))
                  }}
                  primaryAction={(
                    <Button
                      $buttonType="primary"
                      background="lightGrey"
                      onClick={() => history.goBack()}
                      disabled={createProductResponse.pending}
                    >
                      Cancel
                    </Button>
                  )}
                  secondaryAction={(
                    <Button
                      $buttonType="submit"
                      type="submit"
                      background="primaryDark"
                      loading={createProductResponse.pending}
                    >
                      Create Product
                    </Button>
                  )}
                />
              </InfoBarWrapper>

              {imageCropperModalFile && (
                <ImageCroppingModal
                  show
                  src={imageCropperModalFile}
                  onDismiss={() => setImageCropperModalFile(null)}
                  onSubmit={(file: File) => {
                    if (file) {
                      setFieldValue("productImage", file);
                      setImageCropperModalFile(null);
                    }
                  }}
                />
              )}
            </Layout>
          </form>
        );
      }}
    </Formik>
  );
};

export const InputSub = styled(Grid.Box)<{ borderright: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  ${({
    theme, width, borderright
  }) => css`
    width: ${width}px;
    background: ${theme.colors.white};
    border-bottom: 1px solid ${theme.colors.lightGrey};
    ${borderright === "true" ? `border-right: 2px solid ${theme.colors.lightGrey}` : `border-left: 2px solid ${theme.colors.lightGrey}`};
    color: ${theme.fonts.colors.primary};
  `}
`;

const SubText = styled.div`
  ${({ theme }) => css`
    p {
      color: ${theme.colors.darkGrey}
      font-style: italic
      font-size: ${({ theme }) => theme.fonts.sizes.p};
    }
  `}
`;

const FormSection = styled.div`
  margin: 20px 0;

  .price-wrapper {
    flex-wrap: wrap;
  }
  .leadTimeGroup-wrapper {
    flex-wrap: wrap;

    div:first-child {
      width: 100%;
    }
  }

  ${({ theme }) => `
    border-bottom: 1px solid ${theme.colors.lightGrey}

    h2 {
      font-size: ${theme.fonts.sizes.h2};
    }
  `};
`;

export default CreateProductPage;
